import { v4 as uuid } from 'uuid'
import { getCenter, getCenterOfBounds, getDistance } from 'geolib'
import DrawModeBase from '@/lib/mapbox/draw-mode-base'
import { findArea, getBestFitUnitOfLength } from '@/lib/math'
import { getItem } from '@/plugins/storage'

/**
 * View modes for shapes.
 */
class ShapeViewMode extends DrawModeBase {
  /**
   * @inheritDoc
   */
  onSetup(shapeGeoJson) {
    const shapeId = shapeGeoJson.properties.id
    const vertexIds = this._generateVertexIds(shapeGeoJson)
    const distanceLabels = this._generateDistanceLabels(shapeGeoJson)
    this._state = {
      shapeId,
      vertexIds,
      distanceLabels,
    }
  }

  /**
   * @inheritDoc
   */
  toDisplayFeatures(geoJson) {
    const shape = this._formatShape(geoJson)
    const vertices = this._formatVertices(shape)
    const features = [shape, ...vertices]

    const areaLabel = this._generateAreaLabel(geoJson)
    if (areaLabel) {
      features.push(areaLabel)
    }
    features.push(...this._state.distanceLabels)

    return features
  }

  /**
   * Generates vertex IDs.
   * @param {Object} shapeGeoJson - GeoJSON feature.
   * @return {Array<string>} Array of generated vertex IDs.
   * @private
   */
  _generateVertexIds(shapeGeoJson) {
    const rings = shapeGeoJson.geometry.coordinates
    if (!rings.length) {
      return []
    }
    return rings[0].map(() => uuid())
  }

  /**
   * Generate the distance labels for a given shape
   * @param {Object} [shapeGeoJson] - GeoJSON feature. If not provided, the current feature is used
   * @return {Array<Object>} Array of GeoJSON features for each label
   * @private
   */
  _generateDistanceLabels(shapeGeoJson) {
    const coordinatesList = shapeGeoJson
      ? shapeGeoJson.geometry.coordinates[0]
      : this._getCoordinates()
    const labels = []

    // First and last vertex in GeoJSON polygon are repeated, so ensure we have at least 3 vertices (one segment)
    // to avoid generating a '0' label
    if (coordinatesList?.length >= 3) {
      // Using for loop here since we're grabbing multiple items in the array
      // during each iteration
      for (let i = 0; i < coordinatesList.length - 1; i++) {
        const coordinates = [coordinatesList[i], coordinatesList[i + 1]]
        const distanceLabel = this._generateDistanceLabel(coordinates)
        labels.push(distanceLabel)
      }
    }

    return labels
  }

  /**
   * Generate the distance label between two give points
   * @param {Array<Array>} coordinates - The two coords to measure the distance between
   * @return {Object} GeoJSON feature for the label
   * @private
   */
  _generateDistanceLabel(coordinates) {
    const unitType = getItem('unitType')
    const distance = getDistance(...coordinates)
    const center = getCenter(coordinates)
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [center.longitude, center.latitude],
      },
      properties: {
        title: getBestFitUnitOfLength({
          length: distance,
          from: 'meters',
          unitType,
        }),
        meta: 'label',
      },
    }
  }

  /**
   * Generate the area labels for a given shape
   * @param {Object} shapeGeoJson - GeoJSON feature
   * @return {Object} GeoJSON feature for the area
   * @private
   */
  _generateAreaLabel(shapeGeoJson) {
    const coordinatesList = shapeGeoJson.geometry.coordinates[0]
    if (coordinatesList?.length < 3) {
      return null
    }
    const unitType = getItem('unitType')

    const area = findArea(coordinatesList, unitType)
    const center = getCenterOfBounds(coordinatesList)
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [center.longitude, center.latitude],
      },
      properties: {
        title: `${area.area}${area.UOM}`,
        meta: 'label',
      },
    }
  }

  /**
   * Initializes the shape coordinates.
   * @param {Object} geoJson
   * @private
   */
  _initCoordinates(geoJson) {
    // shape coordinates are two-dimensional
    geoJson.geometry.coordinates = [[]]
  }

  /**
   * Formats the shape GeoJSON for rendering on the map.
   * @param {Object} shapeGeoJson - Shape feature GeoJSON.
   * @return {Object} Modified shape feature GeoJSON.
   * @private
   */
  _formatShape(shapeGeoJson) {
    if (!this._getFeature()) {
      return shapeGeoJson
    }
    return shapeGeoJson
  }

  /**
   * Formats a list of vertices for rendering on the map.
   * @param {Object} shapeGeoJson - The shape feature GeoJSON.
   * @return {Array<Object>} List of vertex GeoJSON features to render on the
   * map.
   * @private
   */
  _formatVertices(shapeGeoJson) {
    const vertexIds = this._state.vertexIds
    const coordinatesList = shapeGeoJson.geometry.coordinates[0]
    const vertices = []

    for (let i = 0; i < vertexIds.length; i++) {
      const id = vertexIds[i]
      const coordinates = coordinatesList[i]

      vertices.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates,
        },
        properties: {
          id,
          meta: 'vertex',
          color: shapeGeoJson.properties.color,
        },
      })
    }

    return vertices
  }

  /**
   * Returns the shape feature
   * @return {object} Shape feature
   */
  _getFeature() {
    const shapeId = this._state.shapeId
    return this.getFeature(shapeId)
  }

  /**
   * Gets the shape coordinates
   * @return {array} coordinates
   */
  _getCoordinates() {
    const shapeFeature = this._getFeature()
    return shapeFeature?.geometry.coordinates[0]
  }
}

export default ShapeViewMode
